<template>
<div class="contents_855">
  <header-bar active="wash-request"></header-bar>
  <h1 class="title2">{{ $t('washRequest.bagPurchase') }}</h1>

  <div v-if="isInputStep" class="contents_box mg20">
    <table class="form_tab form_line_top">
      <tr>
        <th><p class="item2">{{ $t('bagSize') }}</p></th>
        <td>
          <p>{{ sizeLabel }}{{ $t('size') }}</p>
        </td>
      </tr>
      <tr>
        <th><p class="item2">{{ $t('washRequest.quantity') }}<span class="font_orange">*</span></p></th>
        <td>
          <p>
            <select v-model="count" class="w200" @change="onCountChange()">
              <option v-for="elem in counts" :key="elem.key" :value="elem.key">
                {{ elem.val }}
              </option>
            </select>
          </p>
        </td>
      </tr>
      <tr>
        <th><p class="item2">{{ $t('washRequestOnetime.form.price') }}</p></th>
        <td><p>{{ price | currency('', 0) }}{{ $t('washRequestOnetime.form.priceUnit') }}</p></td>
      </tr>
      <tr>
        <th><p class="item3">{{ $t('paymentMethod') }}</p></th>
        <td>
          <p>{{ paymentMethodLabel }}{{ $t('profile.paymentSuffix') }}</p>
        </td>
      </tr>
    </table>

    <div class="err-msg-area">
      <p>
        <span class="font_orange" v-show="hasPriceLoadError">
          {{ $t('washRequest.bagPurchasePriceUnavailable') }}
        </span>
      </p>
    </div>

    <p class="submit_bx_l">
      <router-link :to="{ name: 'wash-request' }" class="submit1 bg_gray">
        {{ $t('backButton') }}
      </router-link>
    </p>
    <p class="submit_bx_r">
      <button @click="goToConfirm()" class="submit1 bg_green" :disabled="hasPriceLoadError">
        {{ $t('confirmButton') }}
      </button>
    </p>
  </div>

  <div v-else-if="isConfirmStep" class="contents_box mg20">
    <table class="form_tab form_line_top">
      <tr>
        <th><p class="item2">{{ $t('bagSize') }}</p></th>
        <td>
          <p>{{ sizeLabel }}{{ $t('size') }}</p>
        </td>
      </tr>
      <tr>
        <th><p class="item2">{{ $t('washRequest.quantity') }}</p></th>
        <td><p>{{ count }}{{ $t('washRequest.unit') }}</p></td>
      </tr>
      <tr>
        <th><p class="item2">{{ $t('washRequestOnetime.form.price') }}</p></th>
        <td><p>{{ price | currency('', 0) }}{{ $t('washRequestOnetime.form.priceUnit') }}</p></td>
      </tr>
      <tr>
        <th><p class="item3">{{ $t('paymentMethod') }}</p></th>
        <td>
          <p>{{ paymentMethodLabel }}{{ $t('profile.paymentSuffix') }}</p>
        </td>
      </tr>
    </table>

    <div class="err-msg-area">
      <p><span class="font_orange" v-for="(e, i) in serverErrors" :key="i">{{ e }}</span></p>
      <p>
        <span class="font_orange" v-show="isStatusWithdrawn">
          {{ $t('accountWithdrawn') }}
        </span>
      </p>
    </div>

    <p class="submit_bx_l">
      <button @click="backToInput()" class="submit1 bg_gray">
        {{ $t('backButton') }}
      </button>
    </p>
    <p v-if="isCreditCardPayment" class="submit_bx_r">
      <button @click="purchase()" class="submit1 bg_green">
        {{ $t('enterCardInfo') }}
      </button>
    </p>
    <p v-else class="submit_bx_r">
      <button @click="purchase()" class="submit1 submit2 bg_green">
        {{ $t('washRequest.bagPurchaseBuy') }}
      </button>
    </p>

    <form id="payment-form" method="post"
        :action="paymentInfo.url" accept-charset="Shift_JIS">
      <input type="hidden" v-for="elem in paymentInfo.formParams" :key="elem.name"
        :name="elem.name" :value="elem.value">
      <input type="hidden" name="RETURL" :value="paymentInfo.retUrl">
    </form>
  </div>

  <div v-else class="contents_box mg20">
    <p class="complete-msg">{{ $t('washRequest.bagPurchaseComplete') }}</p>
    <p class="submit_bx_c">
      <router-link :to="{ name: 'wash-request' }" class="submit1 bg_gray">
        {{ $t('washRequest.bagPurchaseBackToWashRequest') }}
      </router-link>
    </p>
  </div>
</div>
</template>

<script>
import userApi from '@/api/user'

const MAX_BAG_PURCHASE_COUNT = 10
const STEP = Object.freeze({
  INPUT: 'input',
  CONFIRM: 'confirm',
  COMPLETE: 'complete',
})
const PAYMENT_METHOD_ID = Object.freeze({
  CREDIT_CARD: 1,
})

export default {
  name: 'bag-purchase',
  props: {
    isComplete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: this.isComplete ? STEP.COMPLETE : STEP.INPUT,
      counts: [],
      pricePerCount: 0,
      count: 1,
      price: 0,

      paymentMethodId: 1,
      paymentMethodLabel: '',

      paymentInfo: {
        url: '',
        formParams: [],
        retUrl: '',
      },

      sizeLabel: '',
      hasPriceLoadError: false,
      isStatusWithdrawn: false,
      serverErrors: [],
    }
  },
  computed: {
    isInputStep() {
      return this.step === STEP.INPUT
    },
    isConfirmStep() {
      return this.step === STEP.CONFIRM
    },
    isCreditCardPayment() {
      return this.paymentMethodId === PAYMENT_METHOD_ID.CREDIT_CARD
    },
  },
  async mounted() {
    if (this.step === STEP.COMPLETE) return

    const unitText = this.$t('washRequest.unit')
    this.counts = [...Array(MAX_BAG_PURCHASE_COUNT).keys()].map(e => ({
      key: e + 1,
      val: `${e + 1}${unitText}`,
    }))

    const [me, mst, { data: currentPlan }] = await Promise.all([
      this.$store.dispatch('user/getMe'),
      window.master.$promise,
      userApi.getMyCurrentPlan(),
    ])

    this.paymentMethodId = Number(me.payment_method_id)
    const paymentMethod = mst.lovsMap.payment_method[this.paymentMethodId]
    if (paymentMethod) {
      this.paymentMethodLabel = paymentMethod.val
    }

    const plan = mst.planMap[currentPlan.plan_id]
    const sizeId = plan.size
    this.sizeLabel = mst.lovsMap.size[sizeId].val
    const bagUserPrice = mst.lovsMap.price_for_add_bag_user?.[sizeId]
    if (!bagUserPrice?.val) {
      this.hasPriceLoadError = true
      return
    }
    this.pricePerCount = parseInt(bagUserPrice.val, 10)
    this.onCountChange()
  },
  methods: {
    onCountChange() {
      this.price = this.pricePerCount * this.count
    },
    goToConfirm() {
      this.step = STEP.CONFIRM
    },
    backToInput() {
      this.serverErrors = []
      this.isStatusWithdrawn = false
      this.step = STEP.INPUT
    },
    async purchase() {
      this.isStatusWithdrawn = false
      this.serverErrors = []

      const purchaseParams = {
        count: this.count,
        payment_method_id: this.paymentMethodId,
      }
      try {
        const { data: purchaseResult } = await userApi.bagPurchase(purchaseParams)
        if (this.isCreditCardPayment) {
          this.updatePaymentFormValues(purchaseResult.payment)
          this.$nextTick(() => {
            document.querySelector('#payment-form').submit()
          })
        } else {
          this.step = STEP.COMPLETE
        }
      } catch (e) {
        if (e?.response?.data?.reason === 'status_withdrawn') {
          this.isStatusWithdrawn = true
          this.$store.dispatch('localStorage/set', { key: 'serverToken', val: null })
          return
        }
        const serverErrors = []
        const errRes = e?.response?.data ?? {}
        if (errRes.reason) {
          serverErrors.push(errRes.reason)
        }
        if (typeof errRes.errors === 'object' && errRes.errors !== null) {
          Object.values(errRes.errors).forEach(errorMessages => {
            if (Array.isArray(errorMessages)) {
              serverErrors.push(...errorMessages)
            }
          })
        }
        this.serverErrors = serverErrors
      }
    },
    updatePaymentFormValues(obj) {
      this.paymentInfo.url = obj.url
      this.paymentInfo.formParams = obj.form_params
      this.paymentInfo.retUrl = `${location.protocol}//${location.host}/${obj.ret_url_path}`
    },
  },
}
</script>

<style lang="scss" scoped>
.err-msg-area {
  margin: 30px auto 0;
  text-align: center;
  width: 90%;
}
.complete-msg {
  text-align: center;
  margin: 40px auto;
}
.submit_bx_c {
  text-align: center;
  margin: 20px auto;
}
</style>
